import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';

import { api } from 'lib/request/api';
import { tcEffectiveWorkspaceIdAtom } from 'components/type-curve-workspace/atoms';
import type { SavedWellFilter } from 'types/type-curve-workspace-show';
import type { CrossPlotChartConfig } from '../types';
import {
  tcSavedWellFiltersAtom,
  tcCrossPlotChartsAtom,
  tcFiltersLocalOnlyAtom,
} from './atoms';
import {
  SAVED_WELL_FILTERS_KEY,
  useSavedWellFiltersQuery,
} from './use-saved-well-filters';
import { camelizeDeep } from './saved-well-filter-definition';
import {
  crossPlotToDefinition,
  definitionToCrossPlot,
} from './saved-cross-plot-definition';

// Cross plots are persisted as analog_well_filter rows (kind='cross_plot') on
// the SAME endpoint as wells filters, so they share the query cache key.
const filtersPath = (workspaceId: number) =>
  `type_curve_workspaces/${workspaceId}/header/filters`;
const filterItemPath = (workspaceId: number, id: number) =>
  `${filtersPath(workspaceId)}/${id}`;
const filtersKey = (workspaceId: number | null): QueryKey => [
  SAVED_WELL_FILTERS_KEY,
  workspaceId,
];

function useInvalidateFilters() {
  const queryClient = useQueryClient();
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  return useCallback(
    () =>
      void queryClient.invalidateQueries({
        queryKey: filtersKey(workspaceId),
      }),
    [queryClient, workspaceId]
  );
}

// Persisted cross plots (kind='cross_plot') derived from the shared saved-filters
// mirror atom.
export function useSavedCrossPlots(): SavedWellFilter[] {
  const saved = useAtomValue(tcSavedWellFiltersAtom);
  return useMemo(() => saved.filter((r) => r.kind === 'cross_plot'), [saved]);
}

// ─── CRUD mutations (kind='cross_plot'), mirroring the wells-filter hooks ───

function useCreateCrossPlot() {
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const invalidate = useInvalidateFilters();
  return useMutation<
    SavedWellFilter,
    unknown,
    { name: string; definition: Record<string, unknown> }
  >({
    mutationFn: ({ name, definition }) =>
      api
        .post(filtersPath(workspaceId as number), {
          json: { name, disabled: false, kind: 'cross_plot', definition },
        })
        .json<SavedWellFilter>(),
    onSettled: invalidate,
  });
}

function useUpdateCrossPlot() {
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const invalidate = useInvalidateFilters();
  return useMutation<
    SavedWellFilter,
    unknown,
    { id: number; definition: Record<string, unknown> }
  >({
    mutationFn: ({ id, definition }) =>
      api
        .patch(filterItemPath(workspaceId as number, id), {
          json: { definition },
        })
        .json<SavedWellFilter>(),
    onSettled: invalidate,
  });
}

function useDeleteCrossPlot() {
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const invalidate = useInvalidateFilters();
  return useMutation<unknown, unknown, { id: number }>({
    mutationFn: ({ id }) =>
      api.delete(filterItemPath(workspaceId as number, id)),
    onSettled: invalidate,
  });
}

// Reconcile the client cross-plot charts against the persisted rows: POST new
// charts (no matching row by name), PATCH changed definitions, DELETE rows whose
// chart was removed. Keyed by `name` = the client chart id.
function useCrossPlotSync() {
  const localOnly = useAtomValue(tcFiltersLocalOnlyAtom);
  const saved = useSavedCrossPlots();
  const create = useCreateCrossPlot();
  const update = useUpdateCrossPlot();
  const del = useDeleteCrossPlot();

  return useCallback(
    async (charts: CrossPlotChartConfig[]) => {
      // View-only page: a published workspace, or the Inventory Workspace.
      if (localOnly) return;
      const ops: Promise<unknown>[] = [];
      const byName = new Map(saved.map((r) => [r.name, r]));
      const chartIds = new Set(charts.map((c) => c.id));

      for (const chart of charts) {
        const def = crossPlotToDefinition(chart);
        const row = byName.get(chart.id);
        if (!row) {
          ops.push(create.mutateAsync({ name: chart.id, definition: def }));
        } else {
          const savedDef = camelizeDeep(row.definition ?? {});
          if (JSON.stringify(savedDef) !== JSON.stringify(def)) {
            ops.push(update.mutateAsync({ id: row.id, definition: def }));
          }
        }
      }
      for (const row of saved) {
        if (!chartIds.has(row.name)) {
          ops.push(del.mutateAsync({ id: row.id }));
        }
      }
      await Promise.all(ops);
    },
    [localOnly, saved, create, update, del]
  );
}

// Page-load hydration + change persistence for cross plots. Mount once (next to
// useWellFiltersInit). Hydrates tcCrossPlotChartsAtom from persisted rows, then
// debounced-reconciles subsequent client changes back to the backend.
export function useCrossPlotsInit() {
  const query = useSavedWellFiltersQuery();
  const setCharts = useSetAtom(tcCrossPlotChartsAtom);
  const charts = useAtomValue(tcCrossPlotChartsAtom);
  const chartsRef = useRef(charts);
  chartsRef.current = charts;
  const sync = useCrossPlotSync();
  const initedRef = useRef(false);

  const { data, isSuccess } = query;

  // Hydrate once from persisted cross plots (or persist the seeded defaults).
  useEffect(() => {
    if (initedRef.current || !isSuccess || !data) return;
    initedRef.current = true;
    const rows = data.filter((r) => r.kind === 'cross_plot');
    if (rows.length > 0) {
      setCharts(rows.map((r) => definitionToCrossPlot(r.name, r.definition)));
    } else {
      void sync(chartsRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data]);

  // Persist subsequent client changes (debounced reconcile against saved rows).
  const debouncedSync = useMemo(
    () => debounce((next: CrossPlotChartConfig[]) => void sync(next), 600),
    [sync]
  );
  useEffect(() => {
    if (!initedRef.current) return;
    debouncedSync(charts);
  }, [charts, debouncedSync]);
  useEffect(() => () => debouncedSync.cancel(), [debouncedSync]);

  return query;
}
